import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {ResourceService} from "../../service-data/resource.service";
import {CoverService} from "../../service-data/cover.service";
import {BodyService} from "../../service-data/body.service";
import {Book} from "../../../model/Book";
import {bestAvailableStage, PdfArtifactStatus} from "../../service-ui/pdf-artifact-stage";
import {requestStagedImage, StageWatch} from "../../service-ui/staged-preview-image";

/**
 * Book cover image rendered straight from the **Resource**, not from the Book
 * endpoint.
 *
 * ── Note the endpoint, because it matters for what you are looking at ────
 * This calls `ResourceService.getImageURL`, which serves the ORIGINAL or the
 * PREPARED render — it never serves the standardized one, because it is keyed
 * on the Resource and standardization is a property of the Cover/Body
 * (`docs/architecture/pdf-pipeline.md` §3). So this surface **cannot** show
 * the final artwork, and the stage it reports is capped at PREPARED however
 * far standardization has got.
 *
 * That is why `standardizes` is NOT forced true here, unlike the sibling
 * `ProjectBookCoverImageComponent` which goes through `/book/croppedimageurl`.
 * Claiming a standardize stage we cannot render would put a permanent
 * "preliminary" badge on a finished book that this component is simply not
 * able to improve on.
 *
 * ── What changed on 2026-09-15 ───────────────────────────────────────────
 * Same three defects as the other pre-fix copies — a self-poisoning cache, no
 * retry trigger, and two competing element ids. `requestStagedImage` owns the
 * request path now, and the component reacts to `prepared` changing.
 */
@Component({
  selector: 'app-book-cover-image',
  templateUrl: './book-cover-image.component.html',
  styleUrls: ['./book-cover-image.component.css']
})
export class BookCoverImageComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('bookCoverImage', { static: true }) bookCoverImage;
  @Input() book: Book;

  displayScale = 1.0;

  Math = Math;

  private onDestroySubject: Subject<boolean> = new Subject<boolean>();
  private stageWatch = new StageWatch();

  constructor(private resourceService: ResourceService,
              private coverService: CoverService,
              private bodyService: BodyService) {
  }

  ngOnInit() {
    this.refreshImagePosition();

    this.resourceService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(resource => {
        if (resource && resource.getId && resource.getId() === this.coverResourceId()) {
          this.onStageMayHaveChanged();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.refreshImagePosition();
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  /** Whichever side supplies the cover artwork for this binding. */
  private coverSide(): any {
    if (!this.book) { return null; }
    return this.book.bindingType === 'SADDLE_STITCH' ? this.book.body : this.book.cover;
  }

  private coverResourceId(): number {
    const side = this.coverSide();
    return (side && side.resource_ID) || 0;
  }

  /**
   * Deliberately reports no standardize stage — see the class comment. The
   * Resource endpoint tops out at PREPARED, so PREPARED is final *for this
   * surface* and marking it preliminary would be a badge nothing could clear.
   */
  private artifactStatus(): PdfArtifactStatus {
    const resourceId = this.coverResourceId();
    const resource = resourceId ? this.resourceService.getLocalItem(resourceId) : null;
    return { resourceId: resourceId, prepared: resource ? resource.prepared : undefined };
  }

  private onStageMayHaveChanged(): void {
    if (!this.stageWatch.changed(this.artifactStatus())) {
      return;
    }
    this.refreshImagePosition();
  }

  refreshImagePositionDetails(cachedImage: any) {
    try {
      this.bookCoverImage.nativeElement.style.display = "block";
      this.bookCoverImage.nativeElement.src = cachedImage.src;

      let naturalWidth = cachedImage.naturalWidth;
      if ( !naturalWidth ) {
        naturalWidth = cachedImage.width;
      }

      let naturalHeight = cachedImage.naturalHeight;
      if ( !naturalHeight ) {
        naturalHeight = cachedImage.height;
      }

      const clipWidth = this.bookCoverImage.nativeElement.parentNode.clientWidth - 2; // - 2 for border thickness
      const clipHeight = this.bookCoverImage.nativeElement.parentNode.clientHeight - 2;

      const pageScreenWidth = ( naturalWidth * this.displayScale * this.book.cover.documentScale);
      if ( pageScreenWidth ) {
        this.bookCoverImage.nativeElement.style.width = pageScreenWidth + 'px';
        const pageScreenHeight = ( naturalHeight * this.displayScale * this.book.cover.documentScale);

        this.bookCoverImage.nativeElement.style.height = pageScreenHeight + 'px';

        this.bookCoverImage.nativeElement.style.left = ( ( clipWidth - pageScreenWidth ) / 2 + this.book.cover.horizontalShift * this.displayScale ) + 'px';
        this.bookCoverImage.nativeElement.style.top = ( ( clipHeight - pageScreenHeight ) / 2 - this.book.cover.verticalShift * this.displayScale ) + 'px';

        this.bookCoverImage.nativeElement.style.width = '436px';
        this.bookCoverImage.nativeElement.style.height = '297px';
        this.bookCoverImage.nativeElement.style.left = '-230px';
        this.bookCoverImage.nativeElement.style.top = '0px';
      }
    } catch (error) {
      console.error('Error refreshing image position details:', error);
      return;
    }
  }

  refreshImagePosition() {
    if ( !this.book || !this.book.cover ) {
      return;
    }
    const resourceId = this.coverResourceId();

    if ( !resourceId ) {
      this.bookCoverImage.nativeElement.src = '';
      return;
    }

    requestStagedImage({
      // Shares the `resource-<id>` namespace with the Draft cover thumbnail on
      // purpose: it is the same render of the same Resource, so one cache
      // entry serves both, and `invalidateResourceImages` reaches it.
      baseId: 'resource-' + resourceId,
      stage: bestAvailableStage(this.artifactStatus()),
      fetchUrl: () => this.resourceService.getImageURL(resourceId, 'COLOR', 72, 1),
      onReady: cachedImage => this.refreshImagePositionDetails(cachedImage),
      onUnavailable: () => {
        this.resourceService.ensureProcessedResource(resourceId);
      }
    });
  }
}

